import { useState, useEffect, useRef } from 'react';
import { channelLinkPayload } from '@features/ChannelLocationSelect';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import SmsForm from '@features/ChannelsForms/SmsForm';
import WhatsAppForm from '@features/ChannelsForms/WhatsAppForm';
import SelectChannel from '@features/ChannelsForms/SelectChannel';
import WhatsAppStatus from '@features/ChannelsForms/WhatsAppStatus';
import WhatsAppCheck from '@features/ChannelsForms/WhatsAppCheck';
import LiveChatForm from '@features/ChannelsForms/LiveChatForm';
import LiveChatMessages from '@features/ChannelsForms/LiveChatMessages';
import LiveChatCode from '@features/ChannelsForms/LiveChatHTMLCode';
import FacebookForm from '@features/ChannelsForms/FacebookForm';
import HostedEmailForm, { returnPathPayload } from '@features/ChannelsForms/HostedEmailForm';
import EmailDomainRecords from '@features/ChannelsForms/EmailDomainRecords';
import WhatsAppBusinessForm from '@features/ChannelsForms/WhatsAppBusinessForm';
import { useTranslation, useEditChannelsForm } from '@hooks';
import { channelsActions, alertActions, accountActions } from '@actions';
import { channelsService, hubService } from '@services';
import { WsHubMessage } from '@constants/ws-hub';
import { CHANNEL, STATUS, SmsProviderType, HOSTED_EMAIL_FORM } from '@constants/channels';
import { smsProviderPayload } from '@features/ChannelsForms/SmsForm/payload';
import {
    buildLiveChatTexts,
    toLiveChatPayload,
    validateLiveChatValues,
} from '@features/ChannelsForms/LiveChatForm/payload';
import { PREVIEW_LOCALES } from '@constants/microsites';
import { handleApiError } from '@helpers/api';
import { getBrowserTimeZone } from '@helpers/dates';
import { Modal } from './styles';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';

const ChannelsCreateModal = ({ modal, setModal, channel, setChannel }) => {
    const account = useSelector(state => state.account.account);

    const { t } = useTranslation();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const twillioUid = useRef(uuidv4());

    const { getValues, setValue, control, reset, watch, trigger, isValid } = useEditChannelsForm(
        modal.type === CHANNEL.EMAIL ? HOSTED_EMAIL_FORM : modal.type,
    );
    const isLiveChat = modal.type === CHANNEL.LIVE_CHAT;

    useEffect(() => {
        if (isLiveChat && modal.step === 2 && !getValues('texts')?.length) {
            setValue('texts', buildLiveChatTexts(PREVIEW_LOCALES[account?.locale]), { shouldValidate: true });
        }
    }, [isLiveChat, modal.step]);

    useEffect(() => {
        if (channel?.id) {
            hubService.subscribeToEvents({
                [WsHubMessage.QrCode(channel?.id)]: qrCode => {
                    setModal(prev => ({ ...prev, qrCode: qrCode }));
                },
                [WsHubMessage.ChannelStatus(channel?.id)]: status => {
                    if (status === 'Ready') {
                        setModal(prev => ({ ...prev, step: 4, status: STATUS.SUCCESS }));
                    } else if (status === 'Disabled') {
                        setModal(prev => ({ ...prev, step: 4, status: STATUS.NUMBER_EXISTS }));
                    } else if (status === 'Pending') {
                        setModal(prev => ({ ...prev, step: 3, status: STATUS.PENDING }));
                    }
                },
            });
        }
        return () => {
            if (channel?.id) {
                hubService.unsubscribeFromEvents([
                    WsHubMessage.ChannelStatus(channel?.id),
                    WsHubMessage.QrCode(channel?.id),
                ]);
            }
        };
    }, [channel]);

    const channelMapCb = {
        [CHANNEL.SMS]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelSms({
                    ...(values?.provider === SmsProviderType.Twilio && { id: twillioUid.current }),
                    name: values.name,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                    ...smsProviderPayload(values),
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.WHATSAPP]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelWhatsApp({ ...values, ...channelLinkPayload(values) });
            },
            nextStep: () => setModal(prev => ({ ...prev, step: 3 })),
        },
        [CHANNEL.WHATSAPP_BUSINESS]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelWhatsAppBusiness({
                    ...values,
                    ...channelLinkPayload(values),
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.LIVE_CHAT]: {
            apiCall: (values, id) => {
                const errorCode = validateLiveChatValues(values);
                if (errorCode) {
                    const error = new Error('error');
                    error.errorCode = errorCode;
                    return Promise.reject(error);
                }
                return channelsService.createOrUpdateChannelLiveChat(
                    toLiveChatPayload(values, { id, timeZone: getBrowserTimeZone() }),
                );
            },
            nextStep: newChannel =>
                setModal(prev => ({
                    ...prev,
                    step: 4,
                    liveChatPublicId: newChannel.publicId,
                    liveChatId: newChannel.id,
                })),
        },
        [CHANNEL.FACEBOOK]: {
            apiCall: values => {
                return channelsService.createOrUpdateChannelFacebook({
                    ...values,
                    ...channelLinkPayload(values),
                    saveInstagramReplies: undefined,
                });
            },
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.INSTAGRAM]: {
            apiCall: values =>
                channelsService.createOrUpdateChannelInstagram({ ...values, ...channelLinkPayload(values) }),
            nextStep: () => onCloseModal(),
        },
        [CHANNEL.EMAIL]: {
            apiCall: values => {
                return channelsService.createHostedEmailChannel({
                    name: values.name,
                    email: values.email,
                    returnPathSubdomain: returnPathPayload(values),
                    enableIncomingEmail: !!values.enableIncomingEmail,
                    teamId: values.teamId,
                    ...channelLinkPayload(values),
                });
            },
            nextStep: created =>
                setModal(prev => ({
                    ...prev,
                    step: 3,
                    emailDomain: created.domain,
                    emailChannelId: created.channel.id,
                })),
        },
    };

    const isSubmitStep = isLiveChat ? modal.step >= 2 : modal.step === 2;

    const createChannel = async () => {
        setIsSubmitting(true);
        try {
            const values = getValues();
            const newChannel = await channelMapCb[modal.type].apiCall(values);
            PostHogManager.capture(ProductEvent.ChannelCreated, { channel_type: modal.type });
            if (modal.type === CHANNEL.WHATSAPP) {
                setChannel(newChannel);
            }
            await dispatch(channelsActions.get());
            await dispatch(accountActions.get());
            channelMapCb[modal.type].nextStep(newChannel);

            dispatch(alertActions.success(t('alertMessages.createSuccess')));
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onClickSubmit = async () => {
        switch (true) {
            case modal.step === 1:
            case modal.step === 2 && isLiveChat:
                setModal(prev => ({ ...prev, step: prev.step + 1 }));
                break;
            case modal.step === 2:
            case modal.step === 3 && isLiveChat:
                await createChannel();
                break;
            case modal.step === 4 && isLiveChat:
                if (!modal.liveChatId) {
                    onCloseModal();
                    break;
                }
                setIsSubmitting(true);
                try {
                    await channelMapCb[modal.type].apiCall(getValues(), modal.liveChatId);
                    await dispatch(channelsActions.get());
                    onCloseModal();
                } catch (error) {
                    handleApiError(error);
                } finally {
                    setIsSubmitting(false);
                }
                break;
            case modal.step === 3 && modal.type === CHANNEL.EMAIL:
                onCloseModal();
                break;
            case modal.step === 4 && modal.type === CHANNEL.WHATSAPP:
                try {
                    await dispatch(channelsActions.get());
                    await dispatch(accountActions.get());
                    onCloseModal();
                } catch (error) {
                    handleApiError(error);
                }
                break;
            default:
                break;
        }
    };

    const onClickBack = () => {
        if (modal.step === 2) {
            reset();
        }
        setModal(prev => ({ ...prev, step: prev.step - 1, preselectedNumber: null }));
    };

    const onCloseModal = async () => {
        setModal({
            open: false,
            step: 1,
            type: CHANNEL.SMS,
            status: STATUS.PENDING,
            qrCode: '',
            liveChatPublicId: null,
            liveChatId: null,
        });
        setChannel({});
    };

    const getPrimaryActionText = () => {
        switch (true) {
            case modal.step === 1:
            case modal.step === 2 && modal.type === CHANNEL.WHATSAPP:
            case modal.step === 2 && isLiveChat:
            case modal.step === 3 && isLiveChat:
            case modal.step === 2 && modal.type === CHANNEL.EMAIL:
                return t('buttons.continue');
            case modal.step === 2 && modal.type === CHANNEL.SMS:
            case modal.step === 2 && modal.type === CHANNEL.FACEBOOK:
            case modal.step === 2 && modal.type === CHANNEL.INSTAGRAM:
            case modal.step === 2 && modal.type === CHANNEL.WHATSAPP_BUSINESS:
            case modal.step === 4 && isLiveChat:
            case modal.step === 3 && modal.type === CHANNEL.EMAIL:
            case modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.SUCCESS:
                return t('buttons.done');
            default:
                return '';
        }
    };

    const getChannelText = () => {
        switch (modal.type) {
            case CHANNEL.SMS:
                return t('common.sms');
            case CHANNEL.WHATSAPP:
            case CHANNEL.WHATSAPP_BUSINESS:
                return t('common.whatsApp');
            case CHANNEL.LIVE_CHAT:
                return t('common.liveChatLowercase');
            case CHANNEL.FACEBOOK:
                return t('common.facebookMessenger');
            case CHANNEL.INSTAGRAM:
                return t('common.instagram');
            case CHANNEL.EMAIL:
                return t('common.email');
            default:
                return '';
        }
    };

    const getTitleText = () => {
        if (modal.step === 1) {
            return { title: t('SettingsChannels.firstStepTitle'), subtitle: t('SettingsChannels.firstStepSubtitle') };
        } else if (modal.step === 3 && modal.type === CHANNEL.EMAIL) {
            return {
                title: t('SettingsChannels.verifyDomainTitle', { domain: modal.emailDomain?.domain }),
                subtitle: t('SettingsChannels.verifyDomainSubtitle'),
            };
        } else if (modal.step > 1) {
            return {
                title: t('SettingsChannels.createChannelTitle', { channelType: getChannelText() }),
                subtitle:
                    modal.type === CHANNEL.EMAIL
                        ? t('SettingsChannels.hostedEmailSubtitle')
                        : t('SettingsChannels.editChannelSubtitle', { channelType: getChannelText() }),
            };
        }
        return { title: '', subtitle: '' };
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseModal}
            closeDisabled={isSubmitting}
            title={getTitleText().title}
            subtitle={getTitleText().subtitle}
            onPrimaryAction={
                (modal.step === 3 && modal.type === CHANNEL.WHATSAPP) ||
                (modal.step === 4 && modal.type === CHANNEL.WHATSAPP && modal.status === STATUS.NUMBER_EXISTS)
                    ? null
                    : onClickSubmit
            }
            primaryActionText={getPrimaryActionText()}
            primaryActionDisabled={isSubmitStep && (isSubmitting || !isValid)}
            onAdditionalAction={modal.step === 2 || (modal.step === 3 && isLiveChat) ? onClickBack : null}
            additionalTextButton={t('buttons.back')}
            additionalButtonType="outlined"
            additionalActionDisabled={isSubmitting}
        >
            {modal.step === 1 ? <SelectChannel modal={modal} setModal={setModal} /> : null}
            <FormProvider {...{ control, setValue, getValues, watch, trigger }}>
                {modal.step === 2 && modal.type === CHANNEL.SMS ? (
                    <SmsForm accountLocale={account.locale} twillioUid={twillioUid.current} />
                ) : null}
                {modal.step === 2 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppForm /> : null}
                {modal.step === 2 && modal.type === CHANNEL.WHATSAPP_BUSINESS ? (
                    <WhatsAppBusinessForm preselectedNumber={modal.preselectedNumber} />
                ) : null}
                {modal.step === 2 && isLiveChat ? <LiveChatForm /> : null}
                {modal.step === 3 && isLiveChat ? <LiveChatMessages /> : null}
                {modal.step === 4 && isLiveChat ? (
                    <LiveChatCode liveChatPublicId={modal.liveChatPublicId} liveChatId={modal.liveChatId} />
                ) : null}
                {modal.step === 2 && (modal.type === CHANNEL.FACEBOOK || modal.type === CHANNEL.INSTAGRAM) ? (
                    <FacebookForm type={modal.type} />
                ) : null}
                {modal.step === 2 && modal.type === CHANNEL.EMAIL ? <HostedEmailForm /> : null}
            </FormProvider>
            {modal.step === 3 && modal.type === CHANNEL.EMAIL ? (
                <EmailDomainRecords
                    domain={modal.emailDomain}
                    channelId={modal.emailChannelId}
                    onDomainUpdated={domain => setModal(prev => ({ ...prev, emailDomain: domain }))}
                />
            ) : null}
            {modal.step === 3 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppCheck qrCode={modal.qrCode} /> : null}
            {modal.step === 4 && modal.type === CHANNEL.WHATSAPP ? <WhatsAppStatus status={modal.status} /> : null}
        </Modal>
    );
};

export default ChannelsCreateModal;
